import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, of, from } from "rxjs";
import { catchError, debounceTime, distinctUntilChanged, map, shareReplay, switchMap } from "rxjs/operators";

/** Keep requests small to reduce latency and API failures */
const TRANSLATE_CHUNK_SIZE = 100;
const DEFAULT_DEBOUNCE_MS = 350;

type DetectionConfidence = "high" | "medium" | "low";

export interface TranslationSegment {
  text: string;
  detectedLang: string;
  confidence: DetectionConfidence;
  translatable: boolean;
}

export interface SmartTranslateOptions {
  targetLang: string;
  sourceLangHint?: string;
  skipIfTargetLanguage?: boolean;
  groupByLanguage?: boolean;
}

interface Token {
  text: string;
  isWordLike: boolean;
}

interface SegmentBuild {
  text: string;
  start: number;
  end: number;
}

const DE_HINT_WORDS = new Set([
  "und",
  "der",
  "die",
  "das",
  "ist",
  "nicht",
  "ich",
  "du",
  "sie",
  "wir",
  "mit",
  "für",
  "auf",
  "ein",
  "eine",
  "zu",
  "den",
  "dem",
  "von",
  "im",
  "am",
  "auch",
  "aber",
]);

const FR_HINT_WORDS = new Set([
  "et",
  "le",
  "la",
  "les",
  "de",
  "des",
  "du",
  "est",
  "pas",
  "je",
  "tu",
  "il",
  "elle",
  "nous",
  "vous",
  "avec",
  "pour",
  "sur",
  "dans",
  "une",
  "un",
  "que",
  "qui",
  "mais",
  "beau",
  "belle",
  "bon",
  "bonne",
  "journal",
  "mémoire",
  "numérique",
  "souvenir",
]);

/** Latin extended + hyphen/apostrophe for short phrases (e.g. "Beau voyage", "l'été"). */
const LATIN_WORDISH = /^[A-Za-zà-ÿÀ-ÿœæøåÆØÅßäöüÄÖÜ\u00C0-\u024F'\-]+$/;

/** Merge Latin runs across NBSP, narrow NBSP (U+202F), thin space, etc. — but never across newlines. */
function isHorizontalOnlyWhitespace(gap: string): boolean {
  if (!gap) return false;
  for (let i = 0; i < gap.length; i++) {
    const c = gap.charCodeAt(i);
    if (c === 0x0a || c === 0x0d || c === 0x2028 || c === 0x2029) return false;
    if (!/\s/.test(gap.charAt(i))) return false;
  }
  return true;
}

const HANGUL_TOKEN = /^[가-힣]+$/;

function isHangulWhitespacePhrase(s: string): boolean {
  const t = (s || "").trim();
  if (!t) return false;
  const parts = t.split(/\s+/).filter(part => part.length > 0);
  for (let p = 0; p < parts.length; p++) {
    if (!HANGUL_TOKEN.test(parts[p])) return false;
  }
  return true;
}

const NO_HINT_WORDS = new Set([
  "og",
  "det",
  "den",
  "er",
  "ikke",
  "jeg",
  "du",
  "vi",
  "med",
  "for",
  "på",
  "til",
  "av",
  "en",
  "et",
  "som",
  "har",
  "fra",
  "men",
  "kan",
  "skal",
  "hva",
  "hvor",
]);

/**
 * Split one field into segments so each request `q` is mostly single-language.
 * Plain text: paragraphs, then lines. HTML: multiple <p>...</p> blocks if present; else one segment.
 */
export function splitIntoTranslatableSegments(text: string): { segments: string[]; joinWith: string } {
  if (text == null || !String(text).trim()) {
    return { segments: [], joinWith: "\n\n" };
  }
  const trimmed = String(text).trim();
  const looksLikeHtml = /<[a-z][\s\S]*>/i.test(trimmed);

  if (looksLikeHtml) {
    const pBlocks = trimmed.match(/<p[^>]*>[\s\S]*?<\/p>/gi);
    if (pBlocks && pBlocks.length > 1) {
      return { segments: pBlocks, joinWith: "" };
    }
    return { segments: [trimmed], joinWith: "\n\n" };
  }

  const paras = trimmed
    .split(/\n\s*\n/)
    .map(s => s.trim())
    .filter(Boolean);
  if (paras.length > 1) {
    return { segments: paras, joinWith: "\n\n" };
  }

  const lines = trimmed
    .split(/\n/)
    .map(s => s.trim())
    .filter(Boolean);
  if (lines.length > 1) {
    return { segments: lines, joinWith: "\n" };
  }

  return { segments: [trimmed], joinWith: "\n\n" };
}

@Injectable({
  providedIn: "root",
})
export class TranslateService {
  googleTranslateService =
    "https://translation.googleapis.com/language/translate/v2?key=AIzaSyAi52N3W4dyKheR9Xf5DxF4IHt3Y22GLRs";

  /** Cache by source|target|text */
  private translationCache = new Map<string, string>();
  /** In-flight dedupe: same request shares one promise */
  private inFlightTranslations = new Map<string, Promise<string>>();

  constructor(public http: HttpClient) {}

  public translate(content, target) {
    if (typeof content === "string" || content instanceof String) {
      content = [content];
    }
    const useHtml = (content as string[]).some(s => typeof s === "string" && /<[a-z][\s\S]*>/i.test(s));
    return new Observable(observer => {
      fetch(this.googleTranslateService, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          q: content,
          target: target,
          format: useHtml ? "html" : "text",
        }),
      })
        .then(response => response.json())
        .then(data => {
          if (data.error) {
            observer.error(new Error(data.error.message || "Translation API error"));
            return;
          }
          observer.next(data);
          observer.complete();
        })
        .catch(error => observer.error(error));
    });
  }

  /**
   * Compatibility API kept for existing usages.
   */
  translateBatch(texts: string[], target: string): Observable<string[]> {
    return from(this.translateBatchSmart(texts || [], target, "auto"));
  }

  /**
   * Production-ready smart translation for one mixed-language text.
   */
  async translateTextSmart(input: string, options: SmartTranslateOptions): Promise<string> {
    if (input == null || input === "") return "";
    const targetLang = this.normalizeLang(options.targetLang || "en");
    const skipIfTarget = options.skipIfTargetLanguage !== false;
    const segments = this.splitAndDetect(input, options.sourceLangHint);
    if (segments.length === 0) return input;

    const translatedPieces = await this.translateSegmentsSmart(segments, targetLang, {
      groupByLanguage: options.groupByLanguage !== false,
      skipIfTargetLanguage: skipIfTarget,
    });

    return this.mergeSegmentsPreserveFormat(input, translatedPieces);
  }

  /**
   * Stream helper for real-time typing scenarios.
   * Use on formControl.valueChanges and bind output.
   */
  observeRealtimeTranslation(
    input$: Observable<string>,
    options: SmartTranslateOptions & { debounceMs?: number }
  ): Observable<string> {
    const debounceMs = options.debounceMs != null ? options.debounceMs : DEFAULT_DEBOUNCE_MS;
    return input$.pipe(
      debounceTime(debounceMs),
      distinctUntilChanged(),
      switchMap(text => from(this.translateTextSmart(text || "", options))),
      catchError(() => of("")),
      shareReplay(1)
    );
  }

  /**
   * Translate fields with mixed-language detection + caching.
   */
  translateFieldEntries(
    fields: Array<{ text: string; apply: (translated: string) => void }>,
    target: string
  ): Observable<void> {
    return from(this.translateFieldEntriesInternal(fields, target));
  }

  private async translateFieldEntriesInternal(
    fields: Array<{ text: string; apply: (translated: string) => void }>,
    target: string
  ): Promise<void> {
    if (!fields || fields.length === 0) return;
    const targetLang = this.normalizeLang(target || "en");
    type FieldPack = { original: string; segments: TranslationSegment[]; apply: (s: string) => void };
    type FlatItem = { text: string; shouldTranslate: boolean; key: string };

    const packs: FieldPack[] = [];
    const flat: FlatItem[] = [];
    const keyToFlatIndices = new Map<string, number[]>();

    for (const field of fields) {
      if (!field || field.text == null || !String(field.text).trim()) continue;
      const original = String(field.text);
      const segments = this.splitAndDetect(original);
      packs.push({ original, segments, apply: field.apply });

      segments.forEach(seg => {
        const shouldTranslate = seg.translatable && !(seg.detectedLang === targetLang && seg.confidence === "high");
        const key = this.buildCacheKey(seg.text, "auto", targetLang);
        const idx = flat.length;
        flat.push({ text: seg.text, shouldTranslate, key });
        if (!keyToFlatIndices.has(key)) keyToFlatIndices.set(key, [idx]);
        else keyToFlatIndices.get(key).push(idx);
      });
    }

    if (flat.length === 0) return;

    const keysNeedingTranslation = Array.from(keyToFlatIndices.keys()).filter(key => {
      const idxs = keyToFlatIndices.get(key) || [];
      const hasTranslatableUsage = idxs.some(i => flat[i].shouldTranslate);
      return hasTranslatableUsage && this.translationCache.get(key) == null;
    });

    if (keysNeedingTranslation.length > 0) {
      const uncachedTexts = keysNeedingTranslation.map(k => {
        const idx = (keyToFlatIndices.get(k) || [])[0];
        return idx != null ? flat[idx].text : "";
      });
      const translated = await this.translateChunkBySource(uncachedTexts, "auto", targetLang);
      for (let i = 0; i < keysNeedingTranslation.length; i++) {
        const key = keysNeedingTranslation[i];
        const fallback = uncachedTexts[i];
        const txt = i < translated.length ? translated[i] : fallback;
        const value = txt != null && String(txt).length > 0 ? String(txt) : fallback;
        this.translationCache.set(key, value);
      }
    }

    // Rebuild each field with original spacing/formatting preserved.
    let cursor = 0;
    for (const pack of packs) {
      const translatedPieces: string[] = [];
      for (let i = 0; i < pack.segments.length; i++) {
        const f = flat[cursor++];
        if (!f.shouldTranslate) translatedPieces.push(f.text);
        else translatedPieces.push(this.translationCache.get(f.key) || f.text);
      }
      const merged = this.mergeSegmentsPreserveFormat(pack.original, translatedPieces);
      pack.apply(merged);
    }
  }

  /**
   * Batch API for arrays of strings, used by legacy callers.
   */
  private async translateBatchSmart(
    texts: string[],
    target: string,
    sourceLangHint: string = "auto"
  ): Promise<string[]> {
    if (!texts || texts.length === 0) return [];
    const out: string[] = [];
    for (const text of texts) {
      const translated = await this.translateTextSmart(text, {
        targetLang: target,
        sourceLangHint,
        skipIfTargetLanguage: true,
        groupByLanguage: true,
      });
      out.push(translated);
    }
    return out;
  }

  private async translateSegmentsSmart(
    segments: TranslationSegment[],
    targetLang: string,
    options: { groupByLanguage: boolean; skipIfTargetLanguage: boolean }
  ): Promise<string[]> {
    const translated = new Array<string>(segments.length);

    // Keep original for non-translatable or same-target chunks.
    segments.forEach((seg, i) => {
      if (!seg.translatable) translated[i] = seg.text;
      else if (options.skipIfTargetLanguage && seg.detectedLang === targetLang && seg.confidence === "high") {
        translated[i] = seg.text;
      }
    });

    const pendingIndices = segments.map((seg, idx) => ({ seg, idx })).filter(item => translated[item.idx] == null);
    if (pendingIndices.length === 0) return translated;

    if (!options.groupByLanguage) {
      for (const item of pendingIndices) {
        translated[item.idx] = await this.translateSingleWithCache(item.seg.text, item.seg.detectedLang, targetLang);
      }
      return translated;
    }

    // Group contiguous segments by language for optional optimization while preserving order.
    const groups: Array<{ lang: string; indices: number[]; texts: string[] }> = [];
    for (const item of pendingIndices) {
      const prev = groups.length > 0 ? groups[groups.length - 1] : null;
      if (prev && prev.lang === item.seg.detectedLang) {
        prev.indices.push(item.idx);
        prev.texts.push(item.seg.text);
      } else {
        groups.push({
          lang: item.seg.detectedLang,
          indices: [item.idx],
          texts: [item.seg.text],
        });
      }
    }

    for (const group of groups) {
      const results = await this.translateChunkBySource(group.texts, group.lang, targetLang);
      results.forEach((txt, i) => {
        translated[group.indices[i]] = txt;
      });
    }

    return translated.map((v, i) => (v == null ? segments[i].text : v));
  }

  /**
   * Google call: source provided explicitly for robust mixed-language translation.
   */
  private async translateChunkBySource(q: string[], sourceLang: string, targetLang: string): Promise<string[]> {
    if (!q || q.length === 0) return [];
    const out: string[] = [];

    for (let i = 0; i < q.length; i += TRANSLATE_CHUNK_SIZE) {
      const chunk = q.slice(i, i + TRANSLATE_CHUNK_SIZE);
      const chunkTranslated = await this.callGoogleTranslate(chunk, sourceLang, targetLang);
      out.push(...chunkTranslated);
    }

    return out;
  }

  private async translateSingleWithCache(text: string, sourceLang: string, targetLang: string): Promise<string> {
    const key = this.buildCacheKey(text, sourceLang, targetLang);
    const cached = this.translationCache.get(key);
    if (cached != null) return cached;

    const inFlight = this.inFlightTranslations.get(key);
    if (inFlight) return inFlight;

    const task = this.callGoogleTranslate([text], sourceLang, targetLang).then(arr => {
      const value = arr[0] != null ? arr[0] : text;
      this.translationCache.set(key, value);
      this.inFlightTranslations.delete(key);
      return value;
    });
    this.inFlightTranslations.set(key, task);
    return task;
  }

  private buildCacheKey(text: string, sourceLang: string, targetLang: string): string {
    return `${sourceLang}|${targetLang}|${text}`;
  }

  private async callGoogleTranslate(q: string[], sourceLang: string, targetLang: string): Promise<string[]> {
    const resp = await fetch(this.googleTranslateService, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        q,
        source: sourceLang === "auto" ? undefined : sourceLang,
        target: targetLang,
        format: "text",
      }),
    });
    const data = await resp.json();
    if (data && data.error) {
      throw new Error(data.error.message || "Translation API error");
    }
    const translations = (data && data.data && data.data.translations) || [];
    const out: string[] = [];
    for (let i = 0; i < q.length; i++) {
      const row = translations[i] as { translatedText?: string } | undefined;
      const txt = row && row.translatedText != null ? String(row.translatedText) : "";
      // If the API returns fewer rows than inputs, or an empty string, keep alignment and avoid leaving keys uncached (which would look like random untranslated fragments).
      out.push(txt.length > 0 ? txt : q[i]);
    }
    return out;
  }

  /**
   * Split text into "word-like" and separator tokens, then build translation segments.
   * Formatting is preserved by later merge via original offsets.
   */
  private splitAndDetect(input: string, sourceLangHint?: string): TranslationSegment[] {
    const segments: TranslationSegment[] = [];
    const tokenToSegment = this.getWordSegmentSpans(input);
    tokenToSegment.forEach(segBuild => {
      const text = segBuild.text;
      const detect = this.detectLanguage(text, sourceLangHint);
      segments.push({
        text,
        detectedLang: detect.lang,
        confidence: detect.confidence,
        translatable: this.isTranslatableText(text),
      });
    });

    return segments;
  }

  /** Same spans used when merging translated pieces back into the original string. */
  private getWordSegmentSpans(original: string): SegmentBuild[] {
    const tokens = this.tokenizeWithSeparators(original);
    const raw = this.buildWordLikeSegments(tokens);
    return this.mergeWhitespaceAdjacentLatinSegments(raw, original);
  }

  /**
   * Join word runs split only by horizontal whitespace (tokenizer used \\s+) so one phrase is one API string:
   * Latin e.g. "Journal de mémoire numérique", Hangul e.g. "디지털 메모리 로그".
   */
  private mergeWhitespaceAdjacentLatinSegments(segments: SegmentBuild[], original: string): SegmentBuild[] {
    if (segments.length <= 1) return segments;
    const isLatinWhitespacePhrase = (s: string): boolean => {
      const t = (s || "").trim();
      if (!t) return false;
      const parts = t.split(/[ \t\u00A0]+/);
      for (let p = 0; p < parts.length; p++) {
        if (!LATIN_WORDISH.test(parts[p])) return false;
      }
      return true;
    };

    const out: SegmentBuild[] = [];
    let acc: SegmentBuild | null = null;

    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i];
      if (!acc) {
        acc = { text: seg.text, start: seg.start, end: seg.end };
        continue;
      }
      const gap = original.slice(acc.end, seg.start);
      const mergedText = acc.text + gap + seg.text;
      if (
        isHorizontalOnlyWhitespace(gap) &&
        (isLatinWhitespacePhrase(mergedText) || isHangulWhitespacePhrase(mergedText))
      ) {
        acc = { text: mergedText, start: acc.start, end: seg.end };
      } else {
        out.push(acc);
        acc = { text: seg.text, start: seg.start, end: seg.end };
      }
    }
    if (acc) out.push(acc);
    return out;
  }

  private tokenizeWithSeparators(input: string): Token[] {
    const tokens: Token[] = [];
    if (!input) return tokens;

    // Keep full URLs intact (so we never translate/alter them).
    const urlRe = /(https?:\/\/[^\s<>"']+)/g;
    let lastIndex = 0;
    let match: RegExpExecArray;

    const tokenizeNonUrl = (part: string) => {
      if (!part) return;
      // keeps separators (spaces, punctuation) in token list for formatting
      const re = /(\s+|[.,!?;:()\[\]{}"'`~@#$%^&*+=<>\\/|]+)/g;
      let last = 0;
      let m: RegExpExecArray;
      while ((m = re.exec(part)) !== null) {
        const idx = m.index;
        if (idx > last) {
          tokens.push({ text: part.slice(last, idx), isWordLike: true });
        }
        tokens.push({ text: m[0], isWordLike: false });
        last = idx + m[0].length;
      }
      if (last < part.length) {
        tokens.push({ text: part.slice(last), isWordLike: true });
      }
    };

    while ((match = urlRe.exec(input)) !== null) {
      const url = match[0];
      const start = match.index;

      if (start > lastIndex) {
        tokenizeNonUrl(input.slice(lastIndex, start));
      }
      // URLs must not merge with adjacent word-like runs (e.g. "中文：https://..."
      // would become one segment and isTranslatableText would skip the whole chunk).
      tokens.push({ text: url, isWordLike: false });
      lastIndex = start + url.length;
    }

    if (lastIndex < input.length) {
      tokenizeNonUrl(input.slice(lastIndex));
    }

    return tokens;
  }

  private buildWordLikeSegments(tokens: Token[]): SegmentBuild[] {
    const segments: SegmentBuild[] = [];
    let cursor = 0;
    let start = -1;
    let buff = "";

    for (const token of tokens) {
      if (token.isWordLike) {
        if (start < 0) start = cursor;
        buff += token.text;
      } else if (start >= 0) {
        segments.push({ text: buff, start: start, end: cursor });
        start = -1;
        buff = "";
      }
      cursor += token.text.length;
    }

    if (start >= 0) {
      segments.push({ text: buff, start: start, end: cursor });
    }

    return segments;
  }

  private mergeSegmentsPreserveFormat(original: string, translatedPieces: string[]): string {
    const segmentOffsets = this.getWordSegmentSpans(original);
    if (segmentOffsets.length !== translatedPieces.length) {
      // Fallback safe path
      return translatedPieces.join(" ");
    }
    let out = "";
    let last = 0;
    segmentOffsets.forEach((seg, i) => {
      out += original.slice(last, seg.start);
      out += translatedPieces[i];
      last = seg.end;
    });
    out += original.slice(last);
    return out;
  }

  /** Regex-based lightweight detector with fallback. */
  private detectLanguage(text: string, sourceLangHint?: string): { lang: string; confidence: DetectionConfidence } {
    if (sourceLangHint && sourceLangHint !== "auto") {
      return { lang: this.normalizeLang(sourceLangHint), confidence: "high" };
    }

    if (/[一-龯ぁ-ゟ゠-ヿ]/.test(text)) return { lang: "zh", confidence: "high" };
    if (/[가-힣]/.test(text)) return { lang: "ko", confidence: "high" };
    if (/[\u0E80-\u0EFF]/.test(text)) return { lang: "lo", confidence: "high" };
    if (/[\u0E00-\u0E7F]/.test(text)) return { lang: "th", confidence: "high" };
    if (/[а-яА-ЯЁё]/.test(text)) return { lang: "ru", confidence: "high" };
    if (/[\u0600-\u06FF]/.test(text)) return { lang: "ar", confidence: "high" };
    if (/[\u0900-\u097F]/.test(text)) return { lang: "hi", confidence: "high" };
    // Vietnamese: only letters that do not overlap Latin-1 French (é è ê in "mémoire" must NOT match vi).
    // Plain "chào" (only U+00E0) falls through to auto and still translates; học/tiếng use U+1EA0–U+1EF9 or đ/ă/ơ/ư.
    if (/[\u0110\u0111\u0102\u0103\u01A0\u01A1\u01AF\u01B0\u1EA0-\u1EF9]/i.test(text)) {
      return { lang: "vi", confidence: "high" };
    }
    // Strong script/diacritics hints first
    if (/[äöüß]/i.test(text)) return { lang: "de", confidence: "high" };
    if (/[àâçéèêëîïôœùûÿ]/i.test(text)) return { lang: "fr", confidence: "high" };
    if (/[æøå]/i.test(text)) return { lang: "no", confidence: "high" };

    // Word-based hints for Latin-only text (German/French/Norwegian support)
    const words = this.extractLatinWords(text);
    if (words.length > 0) {
      const deScore = this.scoreHintWords(words, DE_HINT_WORDS);
      const frScore = this.scoreHintWords(words, FR_HINT_WORDS);
      const noScore = this.scoreHintWords(words, NO_HINT_WORDS);
      const scored = [
        { lang: "de", score: deScore },
        { lang: "fr", score: frScore },
        { lang: "no", score: noScore },
      ].sort((a, b) => b.score - a.score);

      const best = scored[0];
      const second = scored[1];
      // Require enough evidence and clear margin to avoid wrong hard-detect.
      if (best.score >= 2 && best.score >= second.score + 1) {
        const confidence: DetectionConfidence = best.score >= 3 ? "high" : "medium";
        return { lang: best.lang, confidence };
      }
    }

    // Latin words are ambiguous across EN/DE/FR/NO/... => let Google auto-detect.
    if (/[A-Za-z]/.test(text)) return { lang: "auto", confidence: "low" };
    return { lang: "auto", confidence: "low" };
  }

  private extractLatinWords(text: string): string[] {
    const m = (text || "").toLowerCase().match(/[a-zà-ÿœæøåß]+/g);
    return m ? m : [];
  }

  private scoreHintWords(words: string[], hintWords: Set<string>): number {
    let score = 0;
    for (const w of words) {
      if (hintWords.has(w)) score++;
    }
    return score;
  }

  private normalizeLang(lang: string): string {
    if (!lang) return "en";
    const short = lang.toLowerCase().split("-")[0];
    return short || "en";
  }

  /**
   * Skip numbers, IDs, hashes, sku-like or code-like strings.
   */
  private isTranslatableText(text: string): boolean {
    const s = (text || "").trim();
    if (!s) return false;
    // Keep links as-is even if URL is embedded inside the segment (e.g. "link:https://...")
    if (/(https?:\/\/[^\s<>"']+)/i.test(s)) return false;
    if (/^\d+([.,:/-]\d+)*$/.test(s)) return false; // numbers/dates
    if (/^[A-Z0-9_-]{4,}$/.test(s)) return false; // IDs/CODES
    if (/^(0x)?[a-f0-9]{8,}$/i.test(s)) return false; // hash/hex-like
    if (/^[{}[\]();=+\-/*\\<>|`~@#$%^&]+$/.test(s)) return false; // symbols only
    return true;
  }
}
